import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useCurrentEditor } from '@tiptap/react';

export const INPUT_CLASS = 'w-full rounded border bg-white px-3 py-1 text-sm';

export const LABEL_CLASS = 'mb-1 block text-xs font-medium';

export interface NodeViewSettingsPanelFieldButton {
    icon: React.ReactNode;
    onClick: () => void;
    title?: string;
}

export interface NodeViewSettingsPanelTextField {
    label: React.ReactNode;
    type: 'text';
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    error?: string;
    disabled?: boolean;
    searchAttr?: string;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

export interface NodeViewSettingsPanelSelectField {
    label: React.ReactNode;
    type: 'select';
    value: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    options: Array<{ value: string; label: string }>;
    disabled?: boolean;
    searchAttr?: string;
    selectProps?: React.SelectHTMLAttributes<HTMLSelectElement>;
}

export interface NodeViewSettingsPanelCheckboxField {
    label: React.ReactNode;
    type: 'checkbox';
    checked: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

export type NodeViewSettingsPanelField =
    NodeViewSettingsPanelTextField | NodeViewSettingsPanelSelectField | NodeViewSettingsPanelCheckboxField;

export interface NodeViewSettingsPanelProps {
    fields: NodeViewSettingsPanelField[];
    children?: React.ReactNode;
}

const CHECKBOX_CLASS =
    'h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700';

function renderFieldControl(field: NodeViewSettingsPanelField, isEditorDisabled: boolean): React.ReactNode {
    if (field.type === 'text') {
        const hasError = Boolean(field.error);
        return (
            <div>
                <input
                    {...field.inputProps}
                    type="text"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={field.placeholder}
                    disabled={field.disabled || isEditorDisabled}
                    data-search-attr={field.searchAttr}
                    className={`${INPUT_CLASS} ${hasError ? 'border-red-600' : ''}`}
                />
                {field.error ? <div className="mt-1 text-sm text-red-600">{field.error}</div> : null}
            </div>
        );
    }
    if (field.type === 'checkbox') {
        return (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                    {...field.inputProps}
                    type="checkbox"
                    checked={field.checked}
                    onChange={field.onChange}
                    disabled={field.disabled || isEditorDisabled}
                    className={CHECKBOX_CLASS}
                />
                <span>{field.label}</span>
            </label>
        );
    }
    return (
        <select
            {...field.selectProps}
            value={field.value}
            onChange={field.onChange}
            disabled={field.disabled || isEditorDisabled}
            data-search-attr={field.searchAttr}
            className={INPUT_CLASS}
        >
            {field.options.map(option => (
                <option
                    key={option.value}
                    value={option.value}
                >
                    {option.label}
                </option>
            ))}
        </select>
    );
}

export function NodeViewSettingsPanel({ fields, children = null }: NodeViewSettingsPanelProps) {
    const columns = fields.length;
    const editor = useCurrentEditor()?.editor;
    const isEditorDisabled = useIsEditorReadonly(editor);
    return (
        <div
            contentEditable={false}
            className="rounded border-b bg-gray-50 px-4 pt-6 pb-3 dark:bg-gray-800"
        >
            <div className={`grid gap-4 grid-cols-${columns}`}>
                {fields.map((field, index) => (
                    <div key={index + (isEditorDisabled ? 'disabled' : 'enabled')}>
                        {field.type !== 'checkbox' ? <label className={LABEL_CLASS}>{field.label}</label> : null}
                        {renderFieldControl(field, isEditorDisabled)}
                    </div>
                ))}
            </div>
            {children}
        </div>
    );
}
